import { Injectable, Output, EventEmitter } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { MessageFilter } from "../models/message-filter.model";
import { environment } from "../../environments/environment";
import { MessageModel } from "../models/message.model";
import { interval, EMPTY, BehaviorSubject, Subscription } from "rxjs";
import { startWith, switchMap } from "rxjs/operators";
import { MessageOverview } from "../models/message-overview.model";
import { UtilityService } from "./utility.service";


@Injectable({
  providedIn: "root"
})
export class MessageService {
  @Output() updateList: EventEmitter<string> = new EventEmitter();
  @Output() clickNewMessage: EventEmitter<string> = new EventEmitter(); //emit null
  @Output() clickOpenInNewWindow: EventEmitter<string> = new EventEmitter(); //clear main page message

  errors: Map<string, any> = new Map();
  showErrors: boolean = false;
  loading: boolean = false;
  filter: MessageFilter | null = null;
  latestUpdate: Date | null = null;
  messageLength: number = 0;
  messageChange: EventEmitter<MessageOverview[]> = new EventEmitter();
  useAsTemplate: EventEmitter<string> = new EventEmitter();
  applyKlart: EventEmitter<void> = new EventEmitter();
  pollingActive: boolean = false;
  pollingInterval: number = 60000; // 1 minute
  private pollSubscription: Subscription | null = null;

  currentMessage: MessageModel | null = null;
  originalMessage: MessageModel | null = null;

  /* Specs for when opening a message in a new window */
  windowHeight: number = 600; //px
  windowWidth: number = 600; //px
  windowLocation: string = "yes"; // yes | no (display address bar)
  windowScrollbars: string = "yes"; //  yes | no (display scroll bars)
  windowStatus: string = "yes"; //  yes | no (display scroll bars)
  requestHeader: HttpHeaders = new HttpHeaders({ "api-version": "1.0", "app-version": "1.0" });

  private checkboxState = new BehaviorSubject<boolean>(false);
  private _variousSelected = new BehaviorSubject<boolean>(false);
  private messageType = new BehaviorSubject<string>("");
  currentMessageType = this.messageType.asObservable();
  
  constructor(private httpClient: HttpClient) { }

  /**
   * Get all messages with optional filtering and pagination
   */
  getAllMessagesAsync(
    filter?: MessageFilter,
    pagination?: { skip?: number; take?: number; sortBy?: string; sortDescending?: boolean },
    options?: { updateStoredFilter?: boolean }
  ) {
    const filterToUse = filter ?? this.filter ?? new MessageFilter();

    if (filter !== undefined && options?.updateStoredFilter !== false) {
      this.filter = filter;
    } else if (!this.filter) {
      this.filter = filterToUse;
    }

    let params = new HttpParams();
    
    const filterMappings: Array<{
      key: keyof MessageFilter;
      paramName: string;
      transform?: (value: any) => string;
      isArray?: boolean;
    }> = [
      { key: 'messageStatusesList', paramName: 'MessageStatusesList', isArray: true },
      { key: 'from', paramName: 'From', transform: (date: Date) => date.toISOString() },
      { key: 'to', paramName: 'To', transform: (date: Date) => date.toISOString() },
      { key: 'heading', paramName: 'Heading' },
      { key: 'location', paramName: 'Location' },
      { key: 'affects', paramName: 'Affects' },
      { key: 'createdOrUpdatedBy', paramName: 'CreatedOrUpdatedBy' },
      { key: 'lastUpdatedAt', paramName: 'LastUpdatedAt', transform: (date: Date) => date.toISOString() },
      { key: 'messageTypeList', paramName: 'MessageTypeList', isArray: true },
      { key: 'timeIntervalEnabled', paramName: 'TimeIntervalEnabled' },
      { key: 'timeIntervalStart', paramName: 'TimeIntervalStart' },
      { key: 'timeIntervalEnd', paramName: 'TimeIntervalEnd' }
    ];

    filterMappings.forEach(mapping => {
      const value = filterToUse[mapping.key];
      
      if (value !== null && value !== undefined) {
        if (mapping.isArray && Array.isArray(value) && value.length > 0) {
          value.forEach(item => {
            params = params.append(mapping.paramName, item.toString());
          });
        } else if (!mapping.isArray && value) {
          const paramValue = mapping.transform ? mapping.transform(value) : value.toString();
          params = params.append(mapping.paramName, paramValue);
        }
      }
    });

    if (pagination) {
      if (pagination.skip !== undefined) params = params.set('Skip', pagination.skip.toString());
      if (pagination.take !== undefined) params = params.set('Take', pagination.take.toString());
      if (pagination.sortBy) params = params.set('SortBy', pagination.sortBy);
      if (pagination.sortDescending !== undefined) params = params.set('SortDescending', pagination.sortDescending.toString());
    }

    return this.httpClient.get(
      environment.messagesBaseUrl + environment.getMessagesApiSuffix,
      { headers: this.requestHeader, params: params }
    );
  }

  getAppMessagesAsync(modifiedSince?: Date) {
    let params = new HttpParams();
    
    if (modifiedSince) {
      params = params.append('modifiedSince', modifiedSince.toISOString());
    }

    return this.httpClient.get(
      environment.messagesBaseUrl + environment.getAppMessagesApiSuffix,
      { headers: this.requestHeader, params: params }
    );
  }

  getToTMessagesAsync() {
    return this.httpClient.get(
      environment.messagesBaseUrl + environment.getToTMessagesApiSuffix,
      { headers: this.requestHeader }
    );
  }

  pollMessageListHandler(res: any){
    const items = Array.isArray(res) ? res : (res?.items ?? []);
    const messages = UtilityService.mapApiResponseArrayToMessageOverviews(items);

    this.messageLength = res?.total ?? messages.length;
    this.messageChange.emit(messages);
  }

  handleError(error: any){
    console.error(error);
  }
  
  pollMessageList() {
    if (this.pollingActive) {
      return;
    }

    this.pollingActive = true;

    this.pollSubscription = interval(this.pollingInterval)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.getAllMessagesAsync(
            this.filter ?? new MessageFilter(),
            { skip: 0, take: 1 },
            { updateStoredFilter: false }
          )
        )
      )
      .subscribe({
        next: this.pollMessageListHandler.bind(this),
        error: this.handleError.bind(this)
      });
  }

  stopPollMessageList() {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
    this.pollingActive = false;
  }

  getMessageById(id: string) {
    if (id) {
      return this.httpClient.get(
        environment.messagesBaseUrl + environment.getMessagesByIdApiSuffix + `/${id}`,
        { headers: this.requestHeader }
      );
    } else {
      return EMPTY;
    }
  }

  getMessageVersions(id: string) {
    if (id) {
      return this.httpClient.get(
        environment.messagesBaseUrl + environment.getMessagesByIdApiSuffix + `/${id}/versions`,
        { headers: this.requestHeader }
      );
    } else {
      return EMPTY;
    }
  }

  /**
   * Create a new message
   */
  createMessage(message: MessageModel) {
    const requestBody = UtilityService.mapMessageToCreateRequest(message);
    
    return this.httpClient.post(
      environment.messagesBaseUrl + environment.createMessageApiSuffix,
      requestBody,
      { headers: this.requestHeader, observe: 'response' }
    );
  }

  /**
   * Create a new version of an existing message
   */
  createMessageVersion(messageId: string, message: MessageModel, messageStatus?: number) {
    const requestBody = UtilityService.mapMessageToVersionRequest(message);
    
    if (messageStatus !== undefined) {
      requestBody.messageStatus = messageStatus;
    }

    return this.httpClient.post(
      environment.messagesBaseUrl + environment.createMessageVersionApiSuffix + `/${messageId}/versions`,
      requestBody,
      { headers: this.requestHeader }
    );
  }

  /**
   * Close a message
   */
  closeMessage(messageId: string) {
    const url = environment.messagesBaseUrl + `/api/v${environment.messagesApiVersion}/messages/${messageId}/close`;
    
    return this.httpClient.patch(
      url,
      {},
      { headers: this.requestHeader }
    );
  }

  /**
   * Mark a message as read
   */
  markMessageAsRead(messageId: string) {
    return this.httpClient.post(
      environment.messagesBaseUrl + environment.markMessageReadApiSuffix + `/${messageId}/read`,
      {},
      { headers: this.requestHeader }
    );
  }

  /**
   * Legacy method for backwards compatibility - now uses createMessageVersion
   * @deprecated Use createMessageVersion instead
   */
  updateMessage(message: MessageModel) {
    if (!message.id) {
      throw new Error('Message ID is required for creating a new version');
    }
    return this.createMessageVersion(message.id, message);
  }

  setError(property, value) {
    if (this.errors.has(property) && value == null) {
      this.errors.delete(property);
    } else if (value != null) {
      this.errors.set(property, value);
    }
  }

  getError(property) {
    if (this.errors.has(property)) return this.errors.get(property);
  }

  hasChanges() {
    return (
      !this.loading &&
      !UtilityService.equals(this.originalMessage, this.currentMessage)
    );
  }

  getNewMessageID() {
    return "new_message" + new Date().valueOf();
  }

  getNewMessageWindowSpecs() {
    let specsArray = [
      "location=" + this.windowLocation,
      "height=" + this.windowHeight,
      "width=" + this.windowWidth,
      "scrollbars=" + this.windowScrollbars,
      "status=" + this.windowStatus
    ];
    return specsArray.join(',');
  }

  changeMessageType(type: string) {
    this.messageType.next(type);
  }

  getCheckboxState() {
    return this.checkboxState.asObservable();
  }

  setCheckboxState(state: boolean) {
    this.checkboxState.next(state);
  }

  getMessageType() {
    return this.messageType.asObservable();
  }
 
  get isVariousSelected(): boolean {
    return this._variousSelected.value;
  }

  set variousSelected(value: boolean) {
    this._variousSelected.next(value);
  }
}